import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TELR_PAYMENT_MESSAGE_SOURCE,
  clearTelrCheckoutPopupFlag,
  consumeTelrPaymentReturnUrl,
  isTelrCheckoutPopupWindow,
  normalizeTelrPaymentStatus,
  storeTelrPaymentStatus,
  TelrPaymentStatus
} from 'src/app/helper/telr-payment-return.util';
import { CommonService } from 'src/app/services/common/common.service';

/**
 * Lightweight Telr hosted-payment return page.
 * Telr redirects here (`/telr/authorised|declined|cancelled`). Same-tab checkout returns
 * to the plans page with a status query param; legacy popup windows settle on a static screen.
 */
@Component({
  standalone: false,
  selector: 'xa-telr-return',
  templateUrl: './telr-return.component.html',
  styleUrls: [ './telr-return.component.scss' ],})
export class TelrReturnComponent implements OnInit {
  /** Normalized payment status from the route. */
  paymentStatus: TelrPaymentStatus | null = null;

  /** True only during the initial hand-off tick. */
  isProcessing = true;

  /** Whether checkout appears to be running inside a legacy payment popup. */
  isPopupReturn = false;

  /**
   * @param route Active route providing Telr status via `data.status`.
   * @param router Used to return to the plans page after same-tab checkout.
   * @param changeDetectorRef Ensures the status UI renders for popup fallback.
   * @param commonService Clears any global overlay that may have been left on.
   */
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly commonService: CommonService
  ) {}

  /**
   * Stores status, notifies a popup opener when present, then either returns to plans
   * (same-tab) or settles on a closeable status screen (legacy popup).
   */
  ngOnInit(): void {
    this.commonService.dismissBootstrapLoader();
    this.commonService.hideLoading();

    this.paymentStatus = normalizeTelrPaymentStatus(this.route.snapshot.data['status']);
    this.isPopupReturn = isTelrCheckoutPopupWindow();

    if (this.paymentStatus) {
      storeTelrPaymentStatus(this.paymentStatus);
      this.notifyOpener(this.paymentStatus);
    }

    if (this.isPopupReturn) {
      try {
        window.close();
      } catch {
        // Browser often blocks close after a cross-origin Telr redirect.
      }
      this.isProcessing = false;
      clearTelrCheckoutPopupFlag();
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Same-tab checkout: send the user back to plans with the payment status.
    this.navigateToPlans(this.paymentStatus);
  }

  /**
   * Closes a legacy popup, or navigates to plans for same-tab returns.
   */
  onPrimaryAction(): void {
    if (this.isPopupReturn || (window.opener && !window.opener.closed)) {
      window.close();
      return;
    }
    this.navigateToPlans(this.paymentStatus);
  }

  /**
   * Title translation key for the current Telr status.
   * @returns i18n key for the status title.
   */
  get statusTitleKey(): string {
    if (this.paymentStatus === TelrPaymentStatus.Authorised) {
      return 'telr_payment_authorised_title';
    }
    if (this.paymentStatus === TelrPaymentStatus.Declined) {
      return 'telr_payment_declined_title';
    }
    if (this.paymentStatus === TelrPaymentStatus.Cancelled) {
      return 'telr_payment_cancelled_title';
    }
    return 'telr_payment_redirecting';
  }

  /**
   * Message translation key for the current Telr status.
   * @returns i18n key for the status message.
   */
  get statusMessageKey(): string {
    if (this.paymentStatus === TelrPaymentStatus.Authorised) {
      return 'telr_payment_authorised_message';
    }
    if (this.paymentStatus === TelrPaymentStatus.Declined) {
      return 'telr_payment_declined_message';
    }
    if (this.paymentStatus === TelrPaymentStatus.Cancelled) {
      return 'telr_payment_cancelled_message';
    }
    return 'telr_payment_close_window_hint';
  }

  /**
   * Primary button label for popup close vs same-tab continue.
   * @returns i18n key for the action button.
   */
  get primaryActionKey(): string {
    return this.isPopupReturn ? 'telr_payment_close_window' : 'telr_payment_continue_to_plans';
  }

  /**
   * Posts the Telr status to the same-origin opener window when available.
   * @param status Normalized Telr payment status.
   */
  private notifyOpener(status: TelrPaymentStatus): void {
    if (!window.opener || window.opener.closed) {
      return;
    }

    try {
      window.opener.postMessage(
        { source: TELR_PAYMENT_MESSAGE_SOURCE, paymentStatus: status },
        window.location.origin
      );
    } catch {
      // Ignore; opener can still use query-param / postMessage fallbacks.
    }
  }

  /**
   * Navigates to the stored (or default) plans page with an optional status query param.
   * @param status Telr payment status to surface on the plans page, or null.
   */
  private navigateToPlans(status: TelrPaymentStatus | null): void {
    clearTelrCheckoutPopupFlag();
    const returnUrl = consumeTelrPaymentReturnUrl();
    const urlTree = this.router.parseUrl(returnUrl);
    if (status) {
      urlTree.queryParams = { ...urlTree.queryParams, paymentStatus: status };
    }
    void this.router.navigateByUrl(urlTree);
  }
}
